// ----------------------------------------------------------------------- imports externos
import { useCallback, useEffect, useRef } from "react";

/* El marco de un formulario: fondo oscurecido y la tarjeta flotando al medio.
 *
 * ── POR QUÉ ES UN COMPONENTE Y NO UN `<div className="form">` EN CADA UNO ──
 *
 * Porque eran TRECE. Cada formulario del hub abría su propio envoltorio a
 * mano, así que «cerrar» significaba una cosa distinta en cada uno: cuatro
 * escuchaban Esc y nueve no, y ninguno cerraba tocando afuera. Trece copias
 * de un marco son trece formas de cerrarlo.
 *
 * ── SE ABRE AL MEDIO Y NO PEGADO A LA DERECHA ─────────────────────────────
 *
 * Pedido de Igna (28/08/2026): *«No me gusta que se abra en un lateral.
 * Debería ser un popup flotante»*. Era una línea de CSS —`justify-content:
 * right`— y hacía que un formulario de cinco campos apareciera como una
 * columna alta y flaca contra el borde.
 *
 * ── LAS TRES FORMAS DE CERRAR, Y LA QUE HAY QUE CUIDAR ────────────────────
 *
 * La X, Esc y el clic afuera. Las dos primeras son deliberadas; la tercera no
 * siempre, y `useCerrarConEsc` decía justamente eso: *«NO cierra con clic
 * afuera. Acá adentro se está tipeando, y un clic perdido que borra media
 * carga es peor que un botón de más»*.
 *
 * Se hace igual, porque es lo que se pidió, con dos resguardos:
 *
 *   1. EL CLIC TIENE QUE EMPEZAR Y TERMINAR EN EL FONDO. Seleccionar texto de
 *      una casilla y soltar el mouse un centímetro afuera es un `click` sobre
 *      el fondo — y sin esto cerraría el formulario en la mitad de una carga.
 *      Es el accidente más común de los tres.
 *   2. SI HAY ALGO ESCRITO, SE PREGUNTA. Con el formulario en blanco cierra
 *      derecho, que es el caso de «me equivoqué de botón».
 *
 * La X y Esc no preguntan: ésas ya son la respuesta.
 */
const MarcoModal = ({
  children,
  onClose,
  hayCambios = false,
  etiqueta = "Formulario",
}) => {
  // Dónde empezó el clic. Se guarda en una ref y no en estado: cambia entre el
  // `mousedown` y el `click` y volver a dibujar en el medio no aporta nada.
  const empezoAfuera = useRef(false);
  const fondo = useRef(null);

  const cerrar = useCallback(() => {
    if (typeof onClose === "function") onClose();
  }, [onClose]);

  /* Esc, escuchado en el DOCUMENTO y no en el marco: el foco puede estar en
   * cualquier casilla de adentro, o en ninguna si se abrió con el mouse y
   * todavía no se tocó nada. Es lo que ya hacía `useCerrarConEsc`, que sigue
   * existiendo para las pantallas que no son formularios. */
  useEffect(() => {
    const alTeclear = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        cerrar();
      }
    };
    document.addEventListener("keydown", alTeclear);
    return () => document.removeEventListener("keydown", alTeclear);
  }, [cerrar]);

  const alApretar = (e) => {
    empezoAfuera.current = e.target === fondo.current;
  };

  const alSoltar = (e) => {
    if (!empezoAfuera.current || e.target !== fondo.current) return;
    empezoAfuera.current = false;
    if (hayCambios &&
        !window.confirm("Hay datos cargados sin guardar. ¿Cerrar igual?")) {
      return;
    }
    cerrar();
  };

  /* DIBUJA SÓLO EL FONDO. La tarjeta —`.form-content`— y su X siguen siendo
   * de cada formulario: traerlas acá obligaba a desarmar el JSX de los trece,
   * y la X ya la pone `FormHeader`. Este marco resuelve UNA cosa, que es
   * cerrar, y la resuelve para todos. */
  return (
    <div
      className="form"
      ref={fondo}
      onMouseDown={alApretar}
      onClick={alSoltar}
      role="presentation"
      aria-label={etiqueta}
    >
      {children}
    </div>
  );
};

export default MarcoModal;
